/* eslint-disable max-len */
import { Component, h, Element, Prop, State, Watch } from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';
import { responsive } from '../../global/utility/responsive/responsive';

/**
 * Rich text is more exciting than plain text. It supports text formatting, such as bold, italics, and underlining, as well as different fonts, font sizes, and colored text. Rich text documents can also include page formatting options, such as custom page margins, line spacing, and tab widths.
 */
@Component({
  tag: 'ui-richtext',
  styleUrl: 'ui-richtext.scss',
  shadow: true,
})
export class UiRichtext {
  @Element() el: HTMLElement;

  /**
   * Specificies into how many columns we want the content to be divided into.
   */
  @Prop() columns = 1;

  /**
   * Specifies the width of the richtext component.
   */
  @Prop({ reflect: true }) componentWidth: number;

  /**
   * Element should float around other content
   */
  @Prop({ reflect: true }) floating: boolean;

  @Prop({ reflect: true }) variant: 'white';

  @State() transformedContent: string;

  @State() columnsToDisplay = 1;

  @Watch('columns')
  @Watch('componentWidth')
  onColumnsChange() {
    if (
      (this.columns > 1 && this.componentWidth >= 480) ||
      this.columns === 1
    ) {
      this.columnsToDisplay = this.columns;
      return;
    }
    this.columnsToDisplay = this.componentWidth === 360 ? 2 : 1;
  }

  componentWillLoad() {
    const contentObserver = new MutationObserver(() => {
      this.transformHtml(this.el.innerHTML);
    });

    contentObserver.observe(this.el, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  componentDidLoad() {
    responsive(this.el);
  }

  transformHtml(html: string) {
    const pComponent = this.floating
      ? '<$1ui-paragraph columns=0 $2>'
      : '<$1ui-paragraph $2>';
    this.transformedContent = html
      .replace(/<h([1-6])( ?[^>]*)>/g, '<ui-headline level="$1"$2>')
      .replace(/<\/h([1-6])>/g, '</ui-headline>')
      .replace(/<ul[^>]*>/g, '<ui-list>')
      .replace(/<\/(ul|ol)>/g, '</ui-list>')
      .replace(/<a(.|\n)*?<\/a>/g, (match) => {
        const attributes = match.match(/\s.+?=".*?"/g).join(' ');
        const content = match.slice(match.indexOf('>') + 1, -4);
        return `<ui-link ${attributes}>${content}</ui-link>`;
      })
      .replace(/<ol[^>]*>/g, '<ui-list list-style="ordered">')
      .replace(/<(\/?)li( ?[^>]*)>/g, '<$1ui-list-item$2>')
      .replace(/<(\/?)p( ?[^>]*)>/g, pComponent);
  }

  render() {
    return (
      <div
        innerHTML={this.transformedContent}
        style={{
          'column-count': this.floating
            ? '0'
            : this.columnsToDisplay.toString(),
        }}
      ></div>
    );
  }
}

versionComponent(UiRichtext);
